import React, { useState, useEffect, useCallback, useRef } from "react";
import { useUser } from "../context/UserContext";
import { useSubscription } from "../context/SubscriptionContext";
import alertIntelligenceAPI from "../services/AlertIntelligenceAPI";
import AlertsIntelligenceHubDesktop from "./AlertsIntelligenceHubDesktop";
import AlertsIntelligenceHubMobile from "./AlertsIntelligenceHubMobile";
import FamilySharingModal from "./FamilySharingModal";
import useIsMobile from "../../../hooks/useIsMobile";
import "../css/Common.css";
import "./css/AlertsIntelligenceHub.css";

/**
 * Main container for the Alerts Intelligence Hub.
 * Manages navigation, state, and coordinates child components.
 *
 * Views:
 * - dashboard: Main overview with stats and featured alerts
 * - list: Browsable list of alerts
 * - detail: Full alert detail view
 * - category: Filtered by category
 *
 * @param {Function} setHeroContent - Function to navigate to other app sections
 */
function AlertsIntelligenceHub({ setHeroContent }) {
  const { loggedInUser } = useUser();
  const { canShare } = useSubscription();

  // ═══════════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════════

  // View management
  const [currentView, setCurrentView] = useState("dashboard");
  const [previousView, setPreviousView] = useState(null);

  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState(null);

  // Alert list data
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsPagination, setAlertsPagination] = useState({
    page: 0,
    totalPages: 0,
    totalElements: 0,
    hasMore: false,
  });

  // Selected alert for detail view
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [alertDetail, setAlertDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Category filter (for category drill-down)
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Alert filters for API calls
  const [alertFilters, setAlertFilters] = useState({
    categories: [],
    minThreatLevel: null,
    maxThreatLevel: null,
    peakActivityOnly: false,
    unreviewedOnly: false,
    sortBy: "RECENT",
    sortDirection: "DESC",
  });

  // Current filter label for display
  const [filterLabel, setFilterLabel] = useState(null);

  // Categories list
  const [categories, setCategories] = useState([]);

  // Family sharing modal
  const [sharingModalOpen, setSharingModalOpen] = useState(false);
  const [alertToShare, setAlertToShare] = useState(null);

  // User preferences
  const [userPreferences, setUserPreferences] = useState(null);

  // Search state
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState(null);
  const searchContainerRef = useRef(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // DATA FETCHING
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Load dashboard data on mount
   */
  useEffect(() => {
    if (loggedInUser?.uuid) {
      fetchDashboard();
      fetchCategories();
    }
  }, [loggedInUser]);

  /**
   * Handle URL parameter for direct navigation to alert detail
   * (e.g., from notification click)
   */
  useEffect(() => {
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      const alertId = params.get("alertId");
      if (alertId && loggedInUser?.uuid) {
        console.log(
          "[AlertsIntelligenceHub] Direct navigation to alert:",
          alertId,
        );
        navigateTo("detail", { alertId: parseInt(alertId, 10) });
        // Clean up URL after navigation
        window.history.replaceState({}, "", window.location.pathname);
      }
    };

    // Check on mount
    handleUrlChange();

    // Listen for notification navigation events
    window.addEventListener("notification-navigation", handleUrlChange);
    window.addEventListener("url-changed", handleUrlChange);

    return () => {
      window.removeEventListener("notification-navigation", handleUrlChange);
      window.removeEventListener("url-changed", handleUrlChange);
    };
  }, [loggedInUser]);

  /**
   * Fetch main dashboard data
   */
  const fetchDashboard = async () => {
    try {
      setDashboardLoading(true);
      setDashboardError(null);

      const response = await alertIntelligenceAPI.getDashboard();
      setDashboardData(response);

      // Extract user preferences from dashboard response
      if (response.preferences) {
        setUserPreferences(response.preferences);
      }
    } catch (error) {
      console.error("[AlertsIntelligenceHub] Error fetching dashboard:", error);
      setDashboardError("Failed to load alerts. Please try again.");
    } finally {
      setDashboardLoading(false);
    }
  };

  /**
   * Fetch available categories
   */
  const fetchCategories = async () => {
    try {
      const response = await alertIntelligenceAPI.getCategories();
      setCategories(response || []);
    } catch (error) {
      console.error(
        "[AlertsIntelligenceHub] Error fetching categories:",
        error,
      );
    }
  };

  /**
   * Fetch alerts from API
   */
  const fetchAlerts = useCallback(
    async (filters = alertFilters, page = 0) => {
      try {
        setAlertsLoading(true);

        const params = {
          ...filters,
          page,
          size: 20,
        };

        // Add category filter if in category view
        if (selectedCategory && currentView === "category") {
          params.categories = [selectedCategory];
        }

        const response = await alertIntelligenceAPI.searchAlerts(params);

        // Map backend response fields to frontend expectations
        setAlerts(response.alerts || response.content || []);
        setAlertsPagination({
          page: response.currentPage ?? response.number ?? 0,
          totalPages: response.totalPages || 0,
          totalElements: response.totalElements || 0,
          hasMore: response.hasMore ?? !response.last ?? false,
        });
      } catch (error) {
        console.error("[AlertsIntelligenceHub] Error fetching alerts:", error);
      } finally {
        setAlertsLoading(false);
      }
    },
    [alertFilters, selectedCategory, currentView],
  );

  /**
   * Load more alerts (pagination)
   */
  const loadMoreAlerts = () => {
    if (alertsPagination.hasMore && !alertsLoading) {
      fetchAlerts(alertFilters, alertsPagination.page + 1);
    }
  };

  /**
   * Fetch alert detail
   */
  const fetchAlertDetail = async (alertId) => {
    try {
      setDetailLoading(true);

      const response = await alertIntelligenceAPI.getAlertDetail(alertId);
      setAlertDetail(response);
    } catch (error) {
      console.error(
        "[AlertsIntelligenceHub] Error fetching alert detail:",
        error,
      );
    } finally {
      setDetailLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // NAVIGATION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Navigate to a view
   */
  const navigateTo = (view, options = {}) => {
    setPreviousView(currentView);
    setCurrentView(view);

    // Handle specific view options
    if (view === "detail" && options.alertId) {
      setSelectedAlert(options.alertId);
      fetchAlertDetail(options.alertId);
    }

    if (view === "category" && options.category) {
      // Start from clean baseline so category filter is independent
      // of any previous stat card filters (critical, peak, unreviewed, etc.)
      const categoryFilters = {
        categories: [options.category],
        minThreatLevel: null,
        maxThreatLevel: null,
        peakActivityOnly: false,
        unreviewedOnly: false,
        sortBy: alertFilters.sortBy || "RECENT",
        sortDirection: alertFilters.sortDirection || "DESC",
      };
      setSelectedCategory(options.category);
      setAlertFilters(categoryFilters);
      setFilterLabel(null);
      fetchAlerts(categoryFilters);
    }

    if (view === "list") {
      // Always start from a clean baseline so stat card filters
      // are independent of any previous category or filter selection
      const baseFilters = {
        categories: [],
        minThreatLevel: null,
        maxThreatLevel: null,
        peakActivityOnly: false,
        unreviewedOnly: false,
        sortBy: alertFilters.sortBy || "RECENT",
        sortDirection: alertFilters.sortDirection || "DESC",
      };

      // Also clear any leftover category selection
      setSelectedCategory(null);

      // Check for filter options
      if (
        options.minThreatLevel !== undefined ||
        options.maxThreatLevel !== undefined
      ) {
        const newFilters = {
          ...baseFilters,
          minThreatLevel: options.minThreatLevel || null,
          maxThreatLevel: options.maxThreatLevel || null,
        };
        setAlertFilters(newFilters);
        setFilterLabel(options.filterLabel || null);
        fetchAlerts(newFilters);
      } else if (options.peakActivityOnly) {
        const newFilters = {
          ...baseFilters,
          peakActivityOnly: true,
        };
        setAlertFilters(newFilters);
        setFilterLabel("Peak Activity");
        fetchAlerts(newFilters);
      } else if (options.unreviewedOnly) {
        const newFilters = {
          ...baseFilters,
          unreviewedOnly: true,
        };
        setAlertFilters(newFilters);
        setFilterLabel("Unreviewed");
        fetchAlerts(newFilters);
      } else {
        // View All — use baseline as-is
        setAlertFilters(baseFilters);
        setFilterLabel(null);
        fetchAlerts(baseFilters);
      }
    }
  };

  /**
   * Go back to previous view
   */
  const goBack = () => {
    // Reset search state when going back
    setSearchExpanded(false);
    setSearchInput("");
    setActiveSearchTerm(null);
    setFilterLabel(null);

    if (currentView === "detail") {
      setCurrentView(previousView || "dashboard");
      setSelectedAlert(null);
      setAlertDetail(null);
    } else if (
      currentView === "category" ||
      currentView === "list" ||
      currentView === "scammap"
    ) {
      setCurrentView("dashboard");
      setSelectedCategory(null);
    } else {
      setCurrentView("dashboard");
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ACTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Handle viewing an alert
   */
  const handleViewAlert = (alertId) => {
    navigateTo("detail", { alertId });
  };

  /**
   * Handle category selection
   */
  const handleCategorySelect = (category) => {
    navigateTo("category", { category });
  };

  /**
   * Handle marking alert as reviewed
   */
  const handleMarkReviewed = async (alertId) => {
    try {
      await alertIntelligenceAPI.markAsReviewed(alertId);

      // Update local state
      if (alertDetail && alertDetail.id === alertId) {
        setAlertDetail((prev) => ({ ...prev, userReviewed: true }));
      }

      // Refresh dashboard stats
      fetchDashboard();
    } catch (error) {
      console.error("[AlertsIntelligenceHub] Error marking reviewed:", error);
    }
  };

  /**
   * Handle starting training
   */
  const handleStartTraining = async (alertId, trainingUuid) => {
    try {
      await alertIntelligenceAPI.recordTrainingStarted(alertId, trainingUuid);

      // Could navigate to training module here
      console.log("[AlertsIntelligenceHub] Training started:", trainingUuid);
    } catch (error) {
      console.error("[AlertsIntelligenceHub] Error recording training:", error);
    }
  };

  /**
   * Open share modal — only available for Family / Extended Family plans.
   */
  const handleOpenShareModal = (alert) => {
    if (!canShare()) return;
    setAlertToShare(alert);
    setSharingModalOpen(true);
  };

  /**
   * Handle sharing alert with family.
   * Only sends alertId and optional message - backend handles the rest.
   */
  const handleShareAlert = async (alertId, options = {}) => {
    try {
      const response = await alertIntelligenceAPI.shareAlertWithFamily(
        alertId,
        options,
      );
      setSharingModalOpen(false);
      setAlertToShare(null);

      // Refresh dashboard to update share counts
      fetchDashboard();

      return response;
    } catch (error) {
      console.error("[AlertsIntelligenceHub] Error sharing alert:", error);
      throw error; // Let modal handle the error
    }
  };

  /**
   * Handle dismissing an alert
   */
  const handleDismissAlert = async (alertId) => {
    try {
      await alertIntelligenceAPI.dismissAlert(alertId);

      // Remove from local list
      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
    } catch (error) {
      console.error("[AlertsIntelligenceHub] Error dismissing alert:", error);
    }
  };

  /**
   * Refresh all data
   */
  const handleRefresh = () => {
    fetchDashboard();
    if (currentView !== "dashboard") {
      fetchAlerts(alertFilters);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // SEARCH
  // ═══════════════════════════════════════════════════════════════════════════

  // Collapse search when clicking outside the search container
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchExpanded &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        if (!activeSearchTerm) {
          setSearchExpanded(false);
          setSearchInput("");
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchExpanded, activeSearchTerm]);

  /**
   * Toggle search input visibility
   */
  const toggleSearch = () => {
    if (!searchExpanded) {
      // Expanding search
      setSearchExpanded(true);
    } else {
      // Collapsing - only allow if no active search term
      if (!activeSearchTerm) {
        setSearchExpanded(false);
        setSearchInput("");
      }
    }
  };

  /**
   * Handle search submission
   */
  const handleSearch = () => {
    if (searchInput.trim()) {
      const term = searchInput.trim();
      console.log("[AIH] handleSearch called with term:", term);

      setActiveSearchTerm(term);
      // Keep search expanded to show results with search visible
      setSearchExpanded(true);

      // Navigate to list view with search filter
      setPreviousView(currentView);
      setCurrentView("list");
      setFilterLabel(`Search: "${term}"`);

      // Create fresh filters object - don't spread alertFilters to avoid any stale searchText
      const searchFilters = {
        categories: [],
        minThreatLevel: null,
        maxThreatLevel: null,
        peakActivityOnly: false,
        unreviewedOnly: false,
        sortBy: alertFilters.sortBy || "RECENT",
        sortDirection: alertFilters.sortDirection || "DESC",
        searchText: term,
      };

      console.log("[AIH] searchFilters:", searchFilters);

      setAlertFilters(searchFilters);
      fetchAlerts(searchFilters);
    }
  };

  /**
   * Handle search input key press
   */
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter" && searchInput.trim()) {
      handleSearch();
    }
    if (e.key === "Escape") {
      // Only collapse if no active search term
      if (!activeSearchTerm) {
        setSearchExpanded(false);
        setSearchInput("");
      }
    }
  };

  /**
   * Handle search input blur - collapse if empty and no active search
   */
  const handleSearchBlur = () => {
    // Only collapse if input is empty and no active search
    if (!searchInput.trim() && !activeSearchTerm) {
      setSearchExpanded(false);
    }
  };

  /**
   * Clear active search and reset to view all alerts
   */
  const clearSearch = () => {
    setActiveSearchTerm(null);
    setSearchInput("");
    setSearchExpanded(false);
    setFilterLabel(null);

    // Reset filters and fetch all alerts
    const defaultFilters = {
      categories: [],
      minThreatLevel: null,
      maxThreatLevel: null,
      peakActivityOnly: false,
      unreviewedOnly: false,
      sortBy: "RECENT",
      sortDirection: "DESC",
    };
    setAlertFilters(defaultFilters);

    // Stay in list view and show all alerts
    setCurrentView("list");
    fetchAlerts(defaultFilters);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  const isMobile = useIsMobile();

  // Not logged in state
  if (!loggedInUser?.uuid) {
    return (
      <div className="common-container">
        <div className="common-header">
          <h1 className="main-title">Alerts Intelligence Hub</h1>
          <p className="common-subtitle">
            Stay informed about the latest scam threats and protect yourself
            with actionable intelligence
          </p>
        </div>
        <div className="aih-login-prompt">
          <h2>Sign In Required</h2>
          <p>Please sign in to access the Alerts Intelligence Hub.</p>
        </div>
      </div>
    );
  }

  // Shared props forwarded to whichever shell renders the UI.
  const shellProps = {
    currentView,
    selectedCategory,
    categories,
    filterLabel,
    searchExpanded,
    searchInput,
    activeSearchTerm,
    dashboardData,
    dashboardLoading,
    dashboardError,
    alerts,
    alertsLoading,
    alertsPagination,
    selectedAlert,
    alertDetail,
    detailLoading,
    goBack,
    handleViewAlert,
    handleCategorySelect,
    handleMarkReviewed,
    handleStartTraining,
    handleOpenShareModal,
    handleDismissAlert,
    handleRefresh,
    loadMoreAlerts,
    navigateTo,
    toggleSearch,
    handleSearch,
    handleSearchKeyPress,
    handleSearchBlur,
    clearSearch,
    setSearchInput,
    searchContainerRef,
  };

  return (
    <>
      {isMobile ? (
        <AlertsIntelligenceHubMobile {...shellProps} />
      ) : (
        <AlertsIntelligenceHubDesktop {...shellProps} />
      )}

      {/* Family Sharing Modal — rendered here so it sits above both shells */}
      {sharingModalOpen && (
        <FamilySharingModal
          isOpen={sharingModalOpen}
          alert={alertToShare}
          onClose={() => {
            setSharingModalOpen(false);
            setAlertToShare(null);
          }}
          onShare={handleShareAlert}
        />
      )}
    </>
  );
}

export default AlertsIntelligenceHub;
